<template>

    <Head :title="__('Login')" />

    <div class="min-h-screen flex flex-col lg:flex-row bg-white">
        <div class="relative hidden lg:flex lg:flex-1">
            <img :src="startSplash"
                 alt="Login Splash"
                 class="h-full w-full object-cover" />

            <div class="absolute top-6 left-8 flex items-center gap-3 text-white">
                <img :src="loginLogo"
                     alt="Logo"
                     class="h-12 w-auto" />
            </div>
        </div>

        <div class="flex flex-col w-full lg:w-[450px] bg-white min-h-screen lg:min-h-auto">
            <div class="flex lg:hidden items-center justify-center pt-8">
                <img :src="loginLogo"
                     alt="Logo"
                     class="h-10 w-auto" />
            </div>

            <div class="flex-1 flex items-center justify-center px-6 py-10 lg:px-8">
                <div class="w-full max-w-md">
                    <div class="mb-8 text-center">
                        <h1 class="text-2xl font-semibold text-gray-900">{{ __('Two-factor authentication') }}</h1>
                        <p class="mt-2 text-sm text-gray-500">{{ __('Enter the security code from your authenticator app') }}</p>
                    </div>

                    <v-form name="formLogin2Fa"
                            class="space-y-5"
                            @keyup.enter="login">
                        <div class="flex justify-center">
                            <img :src="qrCodeImage"
                                 alt="QR Code"
                                 class="w-[200px] h-[200px] rounded-md border border-gray-200" />
                        </div>

                        <div>
                            <label for="generatedCode"
                                   class="block text-sm font-medium text-gray-700 mb-1">{{ __('Enter security code') }}</label>
                            <InputText id="generatedCode"
                                       type="text"
                                       size="small"
                                       class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-center font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-black-500/50"
                                       v-model="form.generatedCode" />
                        </div>

                        <Button @click="login"
                                :label="__('Login')"
                                :pt="{ icon: { class: 'mr-2 inline-block' } }"
                                icon="pi pi-sign-in"
                                class="w-full justify-center rounded-md bg-secondary-500 px-4 py-2 text-sm font-medium text-white hover:bg-secondary-700 transition-colors" />

                        <div class="text-xs text-gray-600 bg-gray-50 rounded-md p-4 border border-gray-100">
                            <p class="font-medium text-gray-700 mb-2">{{ __('If a QR code appeared above:') }}</p>
                            <ol class="list-decimal pl-5 space-y-1">
                                <li>{{ __('Download the Google Authenticator app') }}
                                    <img :src="gaIcon"
                                         alt="GA"
                                         class="inline-block w-5 h-5 align-middle" />
                                </li>
                                <li>{{ __('Open the app and tap "Add a code"') }}</li>
                                <li>{{ __('Scan the QR code from the screen') }}</li>
                                <li>{{ __('Enter the generated code in the field above') }}</li>
                                <li>{{ __('Click "Login"') }}</li>
                            </ol>
                            <p v-if="!qrCodeImage.includes('google_authenticator_big')"
                               class="font-medium text-gray-700 mt-3 mb-2">{{ __('If a QR code did not appear above:') }}</p>
                            <ol v-if="!qrCodeImage.includes('google_authenticator_big')"
                                class="list-decimal pl-5 space-y-1">
                                <li>{{ __('Open the Google Authenticator app') }}
                                    <img :src="gaIcon"
                                         alt="GA"
                                         class="inline-block w-5 h-5 align-middle" />
                                </li>
                                <li>{{ __('Enter the generated code in the field above') }}</li>
                                <li>{{ __('Click "Login"') }}</li>
                            </ol>
                        </div>
                    </v-form>
                </div>
            </div>

            <footer class="px-4 py-4 text-sm text-gray-500">
                <div class="text-center">
                    <p class="font-light">Powered by © 4Evolve wersja 1.0</p>
                    <p class="text-xs text-gray-400 mt-1">rewizja {{ systemRevision }}</p>
                </div>
            </footer>
        </div>
    </div>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3';
import { useForm } from '@inertiajs/vue3';
import { Head } from '@inertiajs/vue3';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';

import Layout from '@layouts/LayoutNoMenuNoFooter.vue';
import Image from '@helper/Image.vue';
import { __ } from '@helper/utils.js';
import { loginLogo, startSplash } from '@helper/constants.js';

defineOptions({ layout: Layout });

const props = defineProps({
    qrCodeImage: String,
    email: String,
    password: String,
    remember: Boolean,
    removeSession: Boolean,
    ga2FaToken: String,
    gaIcon: String,
});

const page = usePage();
const systemRevision = page.props.systemRevision;

const form = useForm({
    generatedCode: '',
    email: props.email,
    password: props.password,
    remember: props.remember,
    removeSession: props.removeSession,
    ga2FaToken: props.ga2FaToken,
});

const login = () => {
    form.post(route('ga2fa-verify'), {
        onSuccess: () => { },
        onError: (errors) => {
            console.error(errors);
        },
    });
};
</script>
